import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, DollarSign, PauseCircle, Filter, RefreshCw, ArrowUpRight, LineChart as LineChartIcon } from 'lucide-react';
import { toast } from 'sonner';
import PriorityHistoryModal from '@/components/PriorityHistoryModal';

const formatCurrency = (v) =>
  `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const STATUS_LABELS = {
  'Not Started': 'Não Iniciado',
  'Iniciado': 'Iniciado',
  'In Progress': 'Em Andamento',
  'On Hold': 'Pausado - Aguarda Cliente',
  'On Hold - Agency': 'Pausado - Aguarda Análise Órgão',
  'Completed': 'Concluído',
};

const scoreClass = (score) => {
  if (score >= 80) return 'bg-red-100 text-red-700 ring-1 ring-red-300';
  if (score >= 60) return 'bg-orange-100 text-orange-700 ring-1 ring-orange-300';
  if (score >= 30) return 'bg-yellow-100 text-yellow-700 ring-1 ring-yellow-300';
  return 'bg-gray-100 text-gray-600';
};

const scoreLabel = (score) => {
  if (score >= 80) return 'Crítico';
  if (score >= 60) return 'Alto';
  if (score >= 30) return 'Médio';
  return 'Baixo';
};

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <Card className={`p-5 ${accent || ''}`} data-testid={`priority-stat-${label}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">{label}</p>
          <p className="text-3xl font-semibold text-gray-900 mt-1">{value}</p>
        </div>
        <Icon className="w-9 h-9 text-emerald-700/40" />
      </div>
    </Card>
  );
}

function Priority({ user }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [bandFilter, setBandFilter] = useState('all'); // critical | high | medium | low
  const [historyTarget, setHistoryTarget] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setRefreshing(true);
      const r = await api.get('/priority/dashboard');
      setData(r.data);
    } catch (err) {
      toast.error('Erro ao calcular prioridades');
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const triggerPersistAndNotify = useCallback(async () => {
    try {
      setRefreshing(true);
      await api.post('/priority/recalculate');
      toast.success('Scores persistidos. Notificações de novos críticos enviadas.');
      await fetchData();
    } catch (err) {
      const status = err.response?.status;
      toast.error(status === 403 ? 'Apenas administradores podem persistir.' : 'Erro ao persistir');
    } finally {
      setRefreshing(false);
    }
  }, [fetchData]);

  const isAdmin = user?.role?.toUpperCase() === 'ADMIN';

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const projectTypes = useMemo(() => {
    if (!data) return [];
    const set = new Set(data.projects.map((p) => p.project_type).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    let list = data.projects;
    if (typeFilter !== 'all') list = list.filter((p) => p.project_type === typeFilter);
    if (statusFilter !== 'all') list = list.filter((p) => p.status === statusFilter);
    if (bandFilter !== 'all') {
      const ranges = {
        critical: [80, 100],
        high: [60, 79],
        medium: [30, 59],
        low: [0, 29],
      };
      const [lo, hi] = ranges[bandFilter];
      list = list.filter((p) => p.score >= lo && p.score <= hi);
    }
    return list;
  }, [data, typeFilter, statusFilter, bandFilter]);

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">Centro de Prioridades</h1>
            <p className="text-gray-600 mt-1">
              Score 0-100 calculado em tempo real considerando impacto financeiro, urgência regulatória,
              velocidade e foco estratégico.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={fetchData}
              disabled={refreshing}
              data-testid="priority-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Recalcular
            </Button>
            {isAdmin && (
              <Button
                onClick={triggerPersistAndNotify}
                disabled={refreshing}
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="priority-persist-btn"
                title="Salva os scores e dispara notificações de novos críticos. O agendamento diário (07h) faz isso automaticamente."
              >
                Salvar & Notificar
              </Button>
            )}
          </div>
        </div>

        {/* Summary cards */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
          </div>
        ) : data ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard
                icon={DollarSign}
                label="Dinheiro em risco"
                value={formatCurrency(data.money_at_risk)}
                accent="border-l-4 border-emerald-700"
              />
              <StatCard
                icon={AlertTriangle}
                label="Projetos críticos (≥80)"
                value={data.critical_count}
                accent="border-l-4 border-red-600"
              />
              <StatCard
                icon={PauseCircle}
                label="Parados há mais de 10 dias"
                value={data.stuck_count}
                accent="border-l-4 border-orange-500"
              />
            </div>

            {/* Filters */}
            <Card className="p-4">
              <div className="flex flex-wrap items-center gap-3">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600 mr-2">Filtros:</span>

                <Select value={bandFilter} onValueChange={setBandFilter}>
                  <SelectTrigger className="w-44" data-testid="priority-band-filter">
                    <SelectValue placeholder="Faixa" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as faixas</SelectItem>
                    <SelectItem value="critical">Crítico (≥80)</SelectItem>
                    <SelectItem value="high">Alto (60-79)</SelectItem>
                    <SelectItem value="medium">Médio (30-59)</SelectItem>
                    <SelectItem value="low">Baixo (&lt;30)</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-56" data-testid="priority-type-filter">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os tipos</SelectItem>
                    {projectTypes.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-56" data-testid="priority-status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os status</SelectItem>
                    <SelectItem value="Not Started">Não Iniciado</SelectItem>
                    <SelectItem value="Iniciado">Iniciado</SelectItem>
                    <SelectItem value="In Progress">Em Andamento</SelectItem>
                    <SelectItem value="On Hold">Pausado - Aguarda Cliente</SelectItem>
                    <SelectItem value="On Hold - Agency">Pausado - Aguarda Análise Órgão</SelectItem>
                  </SelectContent>
                </Select>

                <span className="ml-auto text-sm text-gray-500">
                  {filtered.length} de {data.total_projects} projetos
                </span>
              </div>
            </Card>

            {/* Projects table */}
            <Card className="p-0 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                      <th className="px-4 py-3 w-20">Score</th>
                      <th className="px-4 py-3">Projeto</th>
                      <th className="px-4 py-3">Cliente</th>
                      <th className="px-4 py-3">Tipo</th>
                      <th className="px-4 py-3">Em aberto</th>
                      <th className="px-4 py-3 hidden lg:table-cell">Motivos</th>
                      <th className="px-4 py-3 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                          Nenhum projeto corresponde aos filtros selecionados.
                        </td>
                      </tr>
                    )}
                    {filtered.map((p) => (
                      <tr
                        key={p.project_id}
                        className="border-b last:border-0 hover:bg-gray-50"
                        data-testid={`priority-row-${p.project_id}`}
                      >
                        <td className="px-4 py-3">
                          <div className={`inline-flex flex-col items-center justify-center w-14 h-14 rounded-full font-bold ${scoreClass(p.score)}`}>
                            <span className="text-lg leading-none">{p.score}</span>
                            <span className="text-[10px] uppercase mt-0.5">{scoreLabel(p.score)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{p.name}</p>
                          <p className="text-xs text-gray-500">
                            {STATUS_LABELS[p.status] || p.status}
                            {p.overall_percentage > 0 && ` · ${Math.round(p.overall_percentage)}%`}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{p.client_name || '—'}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="text-xs">{p.project_type || '—'}</Badge>
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {p.pending_amount > 0 ? formatCurrency(p.pending_amount) : '—'}
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          {p.reasons.length === 0 ? (
                            <span className="text-gray-400 italic text-xs">—</span>
                          ) : (
                            <ul className="space-y-1 text-xs text-gray-700">
                              {p.reasons.slice(0, 3).map((r, i) => (
                                <li key={`${p.project_id}-r-${i}`}>• {r}</li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setHistoryTarget({ id: p.project_id, name: p.name })}
                              data-testid={`priority-history-${p.project_id}`}
                              title="Ver evolução do score"
                            >
                              <LineChartIcon className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => navigate(`/projects/${p.project_id}`)}
                              data-testid={`priority-open-${p.project_id}`}
                              title="Abrir projeto"
                            >
                              <ArrowUpRight className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Footer caption */}
            <p className="text-xs text-gray-400 text-center pt-2">
              Última atualização: {new Date(data.generated_at).toLocaleString('pt-BR')} ·
              {' '}40% Financeiro · 30% Regulatório · 20% Velocidade · 10% Estratégico
            </p>
          </>
        ) : null}
      </div>

      <PriorityHistoryModal
        projectId={historyTarget?.id}
        projectName={historyTarget?.name}
        open={!!historyTarget}
        onOpenChange={(o) => !o && setHistoryTarget(null)}
      />
    </Layout>
  );
}

export default Priority;
